// Wyoming AOIs list — mirrors src/mi/routes/AoisList.tsx but reads
// from /api/mi/wy/aois and pins via the WY-aware POST. Operator
// pinning here creates rows in both mi.aois and mi.wy_aois.

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { wyApi, type WyAoiListItem } from '../services/wyApi';

export function WyAoisList() {
  const [aois, setAois] = useState<WyAoiListItem[]>([]);
  const [err, setErr]   = useState<string | null>(null);
  const [pinOpen, setPinOpen] = useState(false);
  const nav = useNavigate();

  async function reload() {
    try { const r = await wyApi.listAois(); setAois(r.aois); setErr(null); }
    catch (e: any) { setErr(e?.message ?? 'failed to load'); }
  }
  useEffect(() => { reload(); }, []);

  return (
    <div className="px-8 py-7 max-w-[1280px] mx-auto">
      <header className="flex items-end justify-between pb-6 border-b border-border gap-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-text-faint">workspace · wyoming</div>
          <h1 className="font-display text-[28px] leading-tight">AOIs</h1>
          <div className="text-[12px] text-text-dim mt-1.5 font-mono">{aois.length} pinned</div>
        </div>
        <button onClick={() => setPinOpen(true)}
          className="px-3 py-2 text-[12px] bg-accent text-bg font-medium rounded hover:bg-accent-bright">
          + Pin AOI
        </button>
      </header>

      {err && <div className="mt-4 text-[13px] text-red bg-red/10 border border-red/40 rounded px-4 py-3">{err}</div>}

      <div className="mt-6 grid grid-cols-1 gap-3">
        {aois.length === 0 && !err && (
          <div className="bg-bg-card-soft border border-border rounded px-6 py-12 text-center">
            <div className="text-[13px] text-text-dim">No Wyoming AOIs pinned yet.</div>
            <div className="text-[11px] text-text-faint font-mono mt-2">
              Pin a Township + Range + Section set to start tracking ownership in that block.
            </div>
          </div>
        )}
        {aois.map(a => (
          <Link key={a.id} to={`/mi/wy/aoi/${a.id}`}
            className="block bg-bg-card border border-border rounded p-4 hover:border-accent/40 hover:bg-bg-card-soft">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="text-[14px] text-text">{a.name}</div>
                <div className="text-[10px] font-mono text-text-faint mt-1">
                  {a.party_count} {a.party_count === 1 ? 'party' : 'parties'}
                  {a.basin && <span> · {a.basin}</span>}
                  <span> · pinned {a.pinned_at.slice(0, 10)}</span>
                </div>
                <div className="text-[10px] font-mono text-text-dim mt-1 truncate">{(a.trs_keys ?? []).join(' · ')}</div>
              </div>
              <button onClick={e => { e.preventDefault(); nav(`/mi/wy/aoi/${a.id}`); }}
                className="shrink-0 px-3 py-1.5 border border-accent text-accent text-[12px] rounded hover:bg-accent-soft">
                Open →
              </button>
            </div>
          </Link>
        ))}
      </div>

      {pinOpen && (
        <WyPinAoiModal
          onClose={() => setPinOpen(false)}
          onPinned={id => { setPinOpen(false); nav(`/mi/wy/aoi/${id}`); }}
        />
      )}
    </div>
  );
}

// Minimal pin modal: TRS list (manual entry — Township num + dir +
// Range num + dir + section). Operator can add multiple TRS rows.
function WyPinAoiModal(props: { onClose: () => void; onPinned: (id: string) => void }) {
  const [name, setName] = useState('');
  const [basin, setBasin] = useState('');
  const [trs, setTrs] = useState<Array<{ tn: string; td: 'N'|'S'; rn: string; rd: 'E'|'W'; sec: string }>>([
    { tn: '', td: 'N', rn: '', rd: 'W', sec: '' },
  ]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function addRow() { setTrs(prev => [...prev, { tn: '', td: 'N', rn: '', rd: 'W', sec: '' }]); }
  function removeRow(i: number) { setTrs(prev => prev.filter((_, j) => j !== i)); }
  function update(i: number, field: 'tn'|'td'|'rn'|'rd'|'sec', v: string) {
    setTrs(prev => prev.map((r, j) => j === i ? { ...r, [field]: v } : r));
  }

  async function save() {
    setErr(null);
    const cleaned = trs
      .map(r => ({
        township_num: parseInt(r.tn, 10),
        township_dir: r.td,
        range_num:    parseInt(r.rn, 10),
        range_dir:    r.rd,
        section:      parseInt(r.sec, 10),
      }))
      .filter(r => Number.isFinite(r.township_num) && Number.isFinite(r.range_num)
                && Number.isFinite(r.section) && r.section >= 1 && r.section <= 36);
    if (cleaned.length === 0) { setErr('add at least one valid TRS row'); return; }
    setSaving(true);
    try {
      const r = await wyApi.pinAoi({
        name: name.trim() || undefined,
        basin: basin.trim() || null,
        trs: cleaned,
      });
      props.onPinned(r.id);
    } catch (e: any) {
      setErr(e?.message ?? 'pin failed');
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={props.onClose}>
      <div className="bg-bg-card border border-border rounded-lg w-[680px] max-w-[94vw] max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-border flex items-center justify-between shrink-0">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-text-faint">wyoming · pin aoi</div>
            <div className="font-display text-[22px] leading-tight">Pin a new AOI</div>
          </div>
          <button onClick={props.onClose} className="text-text-dim hover:text-text text-2xl leading-none">×</button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div className="grid grid-cols-[1fr_140px] gap-3">
            <input value={name} onChange={e => setName(e.target.value)} placeholder="AOI name (optional — stitched from TRS if blank)"
              className="bg-bg-input border border-border rounded px-3 py-2 text-[13px] outline-none focus:border-accent" />
            <input value={basin} onChange={e => setBasin(e.target.value)} placeholder="basin (PRB, WRB…)"
              className="bg-bg-input border border-border rounded px-3 py-2 text-[13px] outline-none focus:border-accent" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint mb-2">TRS tuples</div>
            {trs.map((r, i) => (
              <div key={i} className="grid grid-cols-[1fr_60px_1fr_60px_1fr_28px] gap-2 mb-1.5">
                <input value={r.tn} onChange={e => update(i, 'tn', e.target.value)} placeholder="T#"
                  className="bg-bg-input border border-border rounded px-2 py-1.5 text-[12px] font-mono outline-none focus:border-accent" />
                <select value={r.td} onChange={e => update(i, 'td', e.target.value)}
                  className="bg-bg-input border border-border rounded px-2 py-1.5 text-[12px] font-mono outline-none focus:border-accent">
                  <option value="N">N</option><option value="S">S</option>
                </select>
                <input value={r.rn} onChange={e => update(i, 'rn', e.target.value)} placeholder="R#"
                  className="bg-bg-input border border-border rounded px-2 py-1.5 text-[12px] font-mono outline-none focus:border-accent" />
                <select value={r.rd} onChange={e => update(i, 'rd', e.target.value)}
                  className="bg-bg-input border border-border rounded px-2 py-1.5 text-[12px] font-mono outline-none focus:border-accent">
                  <option value="W">W</option><option value="E">E</option>
                </select>
                <input value={r.sec} onChange={e => update(i, 'sec', e.target.value)} placeholder="Sec 1-36"
                  className="bg-bg-input border border-border rounded px-2 py-1.5 text-[12px] font-mono outline-none focus:border-accent" />
                <button onClick={() => removeRow(i)} disabled={trs.length === 1}
                  className="text-text-faint hover:text-red disabled:opacity-40 text-lg leading-none">×</button>
              </div>
            ))}
            <button onClick={addRow} className="text-[11px] font-mono text-accent hover:text-accent-bright">+ add TRS row</button>
          </div>
          {err && <div className="text-[12px] text-red bg-red/10 border border-red/40 rounded px-3 py-2">{err}</div>}
        </div>
        <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-2 bg-bg-card-soft/40 shrink-0">
          <button onClick={props.onClose} disabled={saving}
            className="px-4 py-2 text-[13px] text-text-dim hover:text-text border border-border rounded disabled:opacity-50">Cancel</button>
          <button onClick={save} disabled={saving}
            className="px-4 py-2 text-[13px] bg-accent text-bg font-medium rounded hover:bg-accent-bright disabled:opacity-50">
            {saving ? 'Pinning…' : 'Pin AOI'}
          </button>
        </div>
      </div>
    </div>
  );
}
